<template>
    <div>
    <p-search-screen
        ref="webOrdersSearchScreen"
        title="Web Orders"
        api-destination="weborders"
        edit-key="saleId"
        :filters="filters"
        :fields="fields"
        :filterBy="filterBy"
        :use-bootstrap-selection="false"
        :show-set-to="false"
        @selected="onSelection"
        :add-new-button="false"
    >
        <template v-slot:belowSearch>
            <b-row>
             <b-col cols="12"  xl="8" class="d-flex">
                <p-checkbox
                    label="Open"
                    class="ms-1"
                    v-model="filters.isOpen"
                />
                <p-checkbox
                    label="Partial"
                    class="ms-2"
                    v-model="filters.isPartial"
                />
                <p-checkbox
                    label="Backordered"
                    class="ms-2"
                    v-model="filters.isBackordered"
                />
                <p-checkbox
                    label="Complete"
                    class="ms-2"
                    v-model="filters.isComplete"
                />
                <p-checkbox
                    label="Failed"
                    class="ms-2"
                    v-model="filters.isFailed"
                />
                <p-checkbox
                    label="Cancelled"
                    class="ms-2"
                    v-model="filters.isCancelled"
                />                
                <p-checkbox
                    label="Completed at Register"
                    class="ms-2"
                    v-model="filters.isCompletedAtRegister"
                />
             </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" sm="4" xl="2">
                    <p-datepicker
                        class="ms-3"
                        label="Order Date: From"
                        vid="webOrderStartDate"
                        v-model="filters.webOrderStartDate"
                    />
                </b-col>
                <b-col cols="12" sm="4" xl="2">
                    <p-datepicker
                        label="Order Date: To"
                        vid="webOrderEndDate"
                        v-model="filters.webOrderEndDate"
                        rules="dateGreaterOrEqual:@webOrderStartDate"
                        is-end-date
                    />
                </b-col>
                 <b-col cols="12" sm="4" xl="2">
                    <p-select
                        label="Shipping Method"
                        :options="allShippingCodes"
                        v-model="filters.shippingMethod"
                        addEmptyOption
                    />
                </b-col>
                <b-col cols="12" sm="4" xl="2">
                    <p-select
                        label="Tender Type"
                        data-type="tenders"
                        v-model="filters.tenderId"
                        addEmptyOption
                    />
                </b-col>
            </b-row>
        </template>
        <template v-slot:empty="{ hasSearched }">
            <div class="text-center">
                <p>There are no records to show</p>
                <p-button v-if="hasSearched" variant="primary" @click="clearAllFilters();">
                    Search All Locations
                </p-button>
            </div>
        </template>
        <template v-slot:actionButtons>
            <b-dropdown
                text="Print Picklist"
                variant="outline-primary"
                :disabled="isBusy">
                <b-dropdown-item @click="printPicklist(true)">
                    Print New Orders Only
                </b-dropdown-item>
                <b-dropdown-item @click="printPicklist(false)" :disabled="selectedWebOrders.length <= 0">
                    Print All Selected Orders
                </b-dropdown-item>
            </b-dropdown>

            <b-dropdown
                class="mr-2"
                text="Exports"
                variant="outline-primary"
                :disabled="isBusy">
                <b-dropdown-item @click="showItemsNotFulfilledModal()">
                    Items Not Fulfilled
                </b-dropdown-item>                
                <b-dropdown-item  
                    @click="exportShipping()" 
                    :disabled="selectedWebOrders.length <= 0">
                    Web Order Shipping Export
                </b-dropdown-item>
            </b-dropdown>     
            <p-button
                variant="primary"
                @click="clearAllFilters();"
                :is-busy="isBusy">
                    Search All Locations
            </p-button>
        </template>
    </p-search-screen>
    <p-modal
        @ok="exportItemsNotFulfilled"
        size="sm"
        label="Items Not Fulfilled"
        ok-button-label="Export"
        :ok-disabled="!isUnfulfilledOptionSelected"
        ref="itemsNotFulfilledModal"
        name="itemsNotFulfilledModal">
        <template v-slot:default>
            <p>Choose which type of unfulfilled web order items to include in the CSV export.</p>
            <p-checkbox label="Open" v-model="unfulfilledItemsSelection.open" />
            <p-checkbox label="Partial" class="mt-1" v-model="unfulfilledItemsSelection.partial" />
            <p-checkbox label="Backordered" class="mt-1" v-model="unfulfilledItemsSelection.backordered" />
            <p-checkbox label="Partial/Backordered" class="mt-1" v-model="unfulfilledItemsSelection.partialBackordered" />
            <p v-if="!isUnfulfilledOptionSelected" class="text-muted mt-2 mb-0">Please choose an option above</p>
            <hr style="border: 0; border-top: 1px solid #adb5bd; opacity: 1;" />
            <p-checkbox label="Summary (by SKU with quantity)" v-model="unfulfilledItemsSelection.summary" />
        </template>
    </p-modal>
    </div>
</template>

<script>
import { datetimeToLocalTimeFormatter ,currencyFormatter} from '../../components/Common/Formatters.js';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import axios from 'axios';

export default {
    mixins: [SearchStorage],
    data() {
        return {
            isBusy: false,
            filterName: 'WebOrdersFilter',
            selectedWebOrders: [],
            allShippingCodes: [],
            unfulfilledItemsSelection: {
                open: false,
                partial: false,
                backordered: false,
                partialBackordered: false,
                summary: false
            },
            filters: {
                automatic: null,
                perPage: 100,
                sortBy: 'webOrderNumber',
                sortDesc: true,
                searchBy: 'Web Order Number',
                filterByStatus: 'Complete',
                webOrderStartDate: null,
                webOrderEndDate: null,
                shippingMethod: null,
                isOpen: true,
                isPartial: true,
                isBackordered: true,
                isComplete: true,
                isFailed: true,
                isCancelled: true,
                isCompletedAtRegister: true,
                tenderId: null,
                locationId: null
            },
            filterBy: [
                'All',
                'Order Number',
                'Customer Name',
                'Customer Email',
                'Shipping Method',
                'Variant',
                'SKU',
                'Barcode',
                'Description'
            ],
            fields: [
                {
                    key: 'webOrderNumber',
                    sortable: true,
                    label: 'Order Number'
                },
                {
                    key: 'webOrderDate',
                    sortable: true,
                    label: 'Order Capture Date',
                    formatter: datetimeToLocalTimeFormatter
                },
                {
                    key: 'shippingMethod',
                    sortable: true,
                    label: 'Shipping Method',
                },
                {
                    key: 'fulfillmentLocation',
                    sortable: true,
                    label: 'Fulfillment Location',
                },                
                {
                    key: 'tender',
                    label: 'Tender Type',
                },
                {
                    key: 'webOrderTotal',
                    sortable: true,
                    label: 'Captured Order Total',
                    tdClass: 'text-end',
                    thClass: 'text-center',
                    formatter: currencyFormatter,
                },
                {
                    key: 'customerName',
                    sortable: true,
                    label: 'Customer Name'
                },
                {
                    key: 'customerEmail',

                    label: 'Customer Email'
                },
                {
                    key: 'customerPhone',

                    label: 'Customer Phone Number'
                },
                {
                    key: 'webOrderStatus',
                    label: 'Order Status',
                    sortable: true
                },
                {
                    key: 'saleId',
                    hidden: true
                }
            ]
        };
    },
    async beforeMount() {
        this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
        const shippingCodes = await selectListOptionsDataContext.getStoreDropdownData('shippingCodes');
        this.allShippingCodes = (shippingCodes || []).map(option => ({ ...option, disabled: false }));
    },
    computed: {
        isUnfulfilledOptionSelected() {
            return this.unfulfilledItemsSelection.open
                || this.unfulfilledItemsSelection.partial
                || this.unfulfilledItemsSelection.backordered
                || this.unfulfilledItemsSelection.partialBackordered;
        }
    },
    methods: {
        clearAllFilters() {
            this.filters.webOrderStartDate = null;
            this.filters.webOrderEndDate = null;
            this.filters.shippingMethod = null;
            this.filters.isOpen = false;
            this.filters.isPartial = false;
            this.filters.isBackordered = false;
            this.filters.isComplete = false;
            this.filters.isFailed = false;
            this.filters.isCancelled = false;
            this.filters.isCompletedAtRegister = false;
            this.filters.tenderId = null;
            this.filters.locationId = null;
            this.$refs.webOrdersSearchScreen.refreshSearchResults();
        },
        onSelection(webOrders) {
            this.selectedWebOrders = webOrders;
        },
        showItemsNotFulfilledModal() {
            const partialAndBackordered = this.filters.isPartial && this.filters.isBackordered;
            this.unfulfilledItemsSelection.open = this.filters.isOpen;
            this.unfulfilledItemsSelection.partialBackordered = partialAndBackordered;
            this.unfulfilledItemsSelection.partial = this.filters.isPartial && !partialAndBackordered;
            this.unfulfilledItemsSelection.backordered = this.filters.isBackordered && !partialAndBackordered;
            this.$bvModal.show('itemsNotFulfilledModal');
        },
        exportItemsNotFulfilled() {
            this.isBusy = true;
            axios
                .post('/weborders/exportunfulfilled', 
                {
                    open: this.unfulfilledItemsSelection.open,
                    partial: this.unfulfilledItemsSelection.partial,
                    backordered: this.unfulfilledItemsSelection.backordered,
                    partialBackordered: this.unfulfilledItemsSelection.partialBackordered,
                    summary: this.unfulfilledItemsSelection.summary,
                },
                { responseType: 'blob' })
                .then(result => {
                    downloadFileToBrowser(result.data, `ItemsNotFulfilled.csv`, 'application/csv');
                    this.$appSuccess(`Items Not Fulfilled export generated successfully.`);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        exportShipping() {
            this.isBusy = true;
            let selectedWebOrderIds = this.selectedWebOrders.map(obj => obj.saleId);
            axios
                .post('/weborders/exportshipping', selectedWebOrderIds, { responseType: 'blob' })
                .then(result => {
                    downloadFileToBrowser(result.data, `WebOrdersShipping.csv`, 'application/csv');
                    this.$appSuccess(`Web Order Shipping Export generated successfully.`);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },	
        async printPicklist(unprinted) {

            let selectedIds = this.selectedWebOrders.map(obj => obj.saleId);
            var locId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            let apiUrl = `/weborders/exportPrintPickList`;

            this.isBusy = true;

            axios
                .post(
                    apiUrl,
                    {
                        ids: selectedIds,
                        locationId: locId,
                        unprinted: unprinted,
                        unfulfilled: false
                    },
                    { responseType: 'blob' }
                )
                .then(result => {
                    if(result.data.size === 0) {
                        this.$appWarning(`There are no new orders for which to print picklists.`)

                        return;
                    }
                    downloadFileToBrowser(
                        result.data,
                        `Print Picklist`,
                        'application/pdf'
                    );
                    this.$appSuccess(
                            `Print picklist report generated successfully.`
                        )
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>
